import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "next-themes";
import { useToast } from "@/hooks/use-toast";
import { User, Settings, Sun, Moon, Monitor, Eye, EyeOff, MapPin, Chrome, ExternalLink, Home, LayoutDashboard } from "lucide-react";
import { useUserPreference } from "@/hooks/useUserPreference";
import { Switch } from "@/components/ui/switch";
import UserAvatar from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const LANDING_PAGE_OPTIONS = [
  { value: "/", label: "Dashboard" },
  { value: "/my-tasks", label: "My Tasks" },
  { value: "/timesheet", label: "Timesheet" },
  { value: "/projects", label: "Projects" },
  { value: "/analytics", label: "Analytics" },
  { value: "/resources", label: "Resources" },
  { value: "/leave", label: "Leave" },
];

function LandingPagePreference() {
  const [landingPage, setLandingPage] = useUserPreference<string>("dashboard.landing_page", "/");
  return (
    <div>
      <h4 className="text-sm font-semibold mb-1">Landing Page</h4>
      <p className="text-xs text-muted-foreground mb-2">Choose which page opens after login</p>
      <Select value={landingPage} onValueChange={setLandingPage}>
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {LANDING_PAGE_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

const WIDGET_OPTIONS = [
  { id: "quick_access", label: "Quick Access" },
  { id: "latest_news", label: "Latest News" },
  { id: "quick_links", label: "Quick Links" },
  { id: "my_allocation", label: "My Allocation" },
  { id: "effort_summary", label: "Effort Summary" },
  { id: "projects_table", label: "Projects Table" },
  { id: "upcoming_events", label: "Upcoming Events" },
  { id: "training_modules", label: "Training Modules" },
  { id: "team_birthdays", label: "Team Birthdays" },
  { id: "knowledge_highlights", label: "Knowledge Highlights" },
];

function DashboardWidgetsPreference() {
  const [hiddenWidgets, setHiddenWidgets] = useUserPreference<string[]>("dashboard.hidden_widgets", []);
  const toggleWidget = (id: string) => {
    setHiddenWidgets(
      hiddenWidgets.includes(id)
        ? hiddenWidgets.filter((w) => w !== id)
        : [...hiddenWidgets, id]
    );
  };
  return (
    <div>
      <h4 className="text-sm font-semibold mb-1">Dashboard Widgets</h4>
      <p className="text-xs text-muted-foreground mb-2">Toggle which widgets appear on your dashboard</p>
      <div className="space-y-2">
        {WIDGET_OPTIONS.map((w) => (
          <div key={w.id} className="flex items-center justify-between">
            <Label htmlFor={`pref-widget-${w.id}`} className="text-xs cursor-pointer">{w.label}</Label>
            <Switch
              id={`pref-widget-${w.id}`}
              checked={!hiddenWidgets.includes(w.id)}
              onCheckedChange={() => toggleWidget(w.id)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

interface MyAccountDialogProps {
  trigger: React.ReactNode;
  defaultTab?: "profile" | "settings";
}

export default function MyAccountDialog({ trigger, defaultTab = "profile" }: MyAccountDialogProps) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(defaultTab);
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const { toast } = useToast();

  // Password form state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Reset password fields + tab when dialog opens
  useEffect(() => {
    if (open) {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTab(defaultTab);
    }
  }, [open, defaultTab]);

  const passwordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      apiRequest("POST", "/api/user/change-password", data),
    onSuccess: () => {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast({ title: "Password changed", description: "Your password has been updated." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to change password.", variant: "destructive" });
    },
  });

  const handleChangePassword = () => {
    if (!currentPassword) {
      toast({ title: "Error", description: "Current password is required.", variant: "destructive" });
      return;
    }
    if (newPassword.length < 6) {
      toast({ title: "Error", description: "New password must be at least 6 characters.", variant: "destructive" });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: "Error", description: "New passwords do not match.", variant: "destructive" });
      return;
    }
    passwordMutation.mutate({ currentPassword, newPassword });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>My Account</DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => setTab(v as "profile" | "settings")}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="space-y-4 mt-4">
            {/* Avatar & Email (read-only) */}
            <div className="flex items-center gap-3 pb-3 border-b">
              <UserAvatar name={user?.name || "User"} src={user?.photoUrl || undefined} size="lg" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
                <Badge variant="outline" className="mt-1 text-[10px]">{user?.role === 'staff' ? 'Member' : user?.role}</Badge>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              Your profile details are managed by an administrator. To update your name, title, bio, skills, or other
              details, please contact your administrator. You can change your profile photo from your profile page.
            </p>

            <div className="space-y-3">
              {user?.title && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Job Title</p>
                  <p className="text-sm mt-0.5">{user.title}</p>
                </div>
              )}
              {user?.department && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Department</p>
                  <p className="text-sm mt-0.5">{user.department}</p>
                </div>
              )}
              {user?.bio && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Bio</p>
                  <p className="text-sm mt-0.5 leading-relaxed">{user.bio}</p>
                </div>
              )}
              {user?.skills && user.skills.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Skills</p>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {user.skills.map((skill, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{skill}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {user?.defaultWorkLocation && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Default Work Location</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm capitalize">
                      {user.defaultWorkLocation.replace(/_/g, " ")}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="settings" className="space-y-6 mt-4 max-h-[60vh] overflow-y-auto pr-1">
            {/* Theme */}
            <div>
              <h4 className="text-sm font-semibold mb-3">Appearance</h4>
              <div className="flex gap-2">
                {[
                  { value: "light", label: "Light", icon: Sun },
                  { value: "dark", label: "Dark", icon: Moon },
                  { value: "system", label: "System", icon: Monitor },
                ].map(({ value, label, icon: Icon }) => (
                  <Button
                    key={value}
                    variant={theme === value ? "default" : "outline"}
                    size="sm"
                    onClick={() => setTheme(value)}
                    className="flex-1"
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Landing Page */}
            <LandingPagePreference />

            {/* Dashboard Widgets */}
            <DashboardWidgetsPreference />

            {/* Browser Extension */}
            <div>
              <h4 className="text-sm font-semibold mb-3">Browser Extension</h4>
              <p className="text-xs text-muted-foreground mb-2">
                Install the Dynamatix Intranet Chrome extension for quick access to timesheets, tasks, and timer right from your browser.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                asChild
              >
                <a
                  href="https://chromewebstore.google.com/detail/dynamatix-intranet/olkgfajoboemahpjbjfoidiefahchjdi"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Chrome className="h-4 w-4 mr-2" />
                  Install Chrome Extension
                  <ExternalLink className="h-3 w-3 ml-auto" />
                </a>
              </Button>
            </div>

            {/* Change Password */}
            <div>
              <h4 className="text-sm font-semibold mb-3">Change Password</h4>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="current-password">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="current-password"
                      type={showCurrentPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password (min 6 characters)"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password"
                  />
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={handleChangePassword}
                    disabled={!currentPassword || !newPassword || !confirmPassword || passwordMutation.isPending}
                    variant="outline"
                  >
                    {passwordMutation.isPending ? "Changing..." : "Change Password"}
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
